import ReactDom from 'react-dom';

import { Icon as BlueIcon } from '@blueprintjs/core';

import IconLogoImg from '@assets/svg/icon-logo.svg';

import { useMutation, useQuery, useQueryClient } from 'react-query';
import { createMessage } from '@services/queries/messages';
import { useContext, useEffect, useRef, useState } from 'react';
import { checkAndCreateThread } from '@services/queries/onboarding/checkAndCreateThread';
import { NavigationContext } from '@layout/navigation/navigation';
import { useParams } from 'react-router-dom';
import { getMessagesByThread } from '@services/queries/onboarding/getMessagesByThread';
import { IOnboardingGroup } from '@models/OnboardingGroup.model';
import { getIdentificationRequest } from '@services/queries/identificationRequests/getIdentificationRequest';

import {
  Container,
  Overlay,
  Close,
  Header,
  Title,
  TitleContainer,
  Description,
  Messages,
  ChatStatus,
  StatusColor,
  StatusName,
  Message,
  Text,
  RightPoint,
  LeftPoint,
  AuthorContainer,
  AuthorImage,
  AuthorName,
  AuthorAutomated,
  AuthorBeamImage,
  TextInner,
  DisabledText,
  SenderInfo,
  SenderName,
  SenderEmail,
} from './Discussion.styles';
import { ChatTextFormat } from '../ChatTextFormat/ChatTextFormat';
import { DiscussionLoading } from './Discussion.loading';

interface IDiscussion {
  open: boolean;
  onClose: () => void;
  group: IOnboardingGroup;
}

export const Discussion = ({ open, onClose, group }: IDiscussion) => {
  const portalDiv = document.getElementById('portal');

  const { studioId, userName, userEmail } = useContext(NavigationContext);

  const myRef = useRef<HTMLDivElement>(null);

  const params = useParams<{ id: string }>();
  const fileId = params.id;

  const queryClient = useQueryClient();

  const [text, setText] = useState('');
  const [isFullyBottom, setIsFullyBottom] = useState(true);
  const [isFirstRender, setIsFirstRender] = useState(true);
  const [sentNewMessage, setSentNewMessage] = useState(false);

  const { data, isLoading } = useQuery(
    ['messages', group.id],
    async () => {
      const threadResponse = await checkAndCreateThread({
        studioId,
        fileId,
        assetId: group.id,
      });

      const identificationRequest = await getIdentificationRequest({
        threadId: threadResponse.threadId,
      });

      const messages = await getMessagesByThread({
        threadId: threadResponse.threadId,
      });

      return {
        messages,
        threadId: threadResponse.threadId,
        identificationRequest,
      };
    },
    {
      refetchInterval: 5000,
    },
  );

  const scrollToBottom = () => {
    if (myRef.current) {
      myRef.current.scrollTop = myRef.current.scrollHeight;
    }
  };

  const mutationMessage = useMutation(createMessage, {
    onSuccess: () => {
      queryClient.invalidateQueries(['messages']);
      setText('');
      setSentNewMessage(true);
    },
  });

  useEffect(() => {
    if (isFullyBottom || isFirstRender || sentNewMessage) {
      scrollToBottom();
      setIsFirstRender(false);
      setSentNewMessage(false);
    }
  }, [data?.messages, isFullyBottom, isFirstRender]);

  const handleScroll = () => {
    if (myRef.current) {
      const container = myRef.current;
      setIsFullyBottom(
        container.scrollTop + container.clientHeight === container.scrollHeight,
      );
    }
  };

  const getStatusName = () => {
    if (data?.identificationRequest?.status === 'closed') return 'Closed';
    if (data?.identificationRequest?.status === 'resolved') return 'Resolved';
    if (data?.identificationRequest?.status === 'information_needed')
      return 'Information Needed';
    if (data?.identificationRequest?.status === 'confirm_product')
      return 'Confirm Product';
    return 'Open';
  };

  return open && portalDiv
    ? ReactDom.createPortal(
        <Overlay>
          <Container>
            {isLoading ? (
              <DiscussionLoading />
            ) : (
              <>
                <Header>
                  <TitleContainer>
                    <Title>Match Request</Title>
                    <Description>
                      #{data?.identificationRequest?.id}
                    </Description>
                  </TitleContainer>
                  <Close onClick={onClose}>
                    <BlueIcon icon="cross" color="#8F95B2" />
                  </Close>
                </Header>

                <ChatStatus>
                  <StatusColor
                    data-status={data?.identificationRequest?.status}
                  />
                  <StatusName>{getStatusName()}</StatusName>
                </ChatStatus>

                <Messages ref={myRef} onScroll={handleScroll}>
                  {!isLoading && data?.messages && (
                    <>
                      {data.messages.messages.map((message, idx) => {
                        const isAdmin = !!message.admin;
                        const isAutomated = !!message.automated;
                        const previousIsAdmin =
                          !!data.messages.messages[idx - 1]?.admin;

                        return (
                          <Message
                            key={message.id}
                            align={isAdmin ? 'left' : 'right'}
                            isSequence={previousIsAdmin === isAdmin}
                          >
                            {previousIsAdmin !== isAdmin && (
                              <AuthorContainer>
                                {isAdmin ? (
                                  <AuthorBeamImage src={IconLogoImg} />
                                ) : (
                                  <AuthorImage />
                                )}
                                {isAdmin ? (
                                  <AuthorName>BeamON</AuthorName>
                                ) : (
                                  <SenderInfo>
                                    <SenderName>{message.sender}</SenderName>
                                    <SenderEmail>
                                      {message.senderEmail}
                                    </SenderEmail>
                                  </SenderInfo>
                                )}
                                {isAutomated && (
                                  <AuthorAutomated>(Automated)</AuthorAutomated>
                                )}
                              </AuthorContainer>
                            )}

                            <Text align={isAdmin ? 'left' : 'right'}>
                              <TextInner
                                dangerouslySetInnerHTML={{
                                  __html: message.text,
                                }}
                                align={isAdmin ? 'left' : 'right'}
                              />
                              {isAdmin ? <LeftPoint /> : <RightPoint />}
                            </Text>
                          </Message>
                        );
                      })}
                    </>
                  )}
                </Messages>
              </>
            )}

            {data?.identificationRequest &&
              ['resolved', 'closed'].includes(
                data.identificationRequest.status,
              ) && (
                <DisabledText>
                  This Request has been{' '}
                  {data.identificationRequest?.status === 'resolved'
                    ? 'Resolved'
                    : 'Closed'}
                  .
                </DisabledText>
              )}

            <ChatTextFormat
              text={text}
              setText={setText}
              onMutateMessage={() => {
                if (
                  text &&
                  !mutationMessage.isLoading &&
                  data?.threadId &&
                  !['resolved', 'closed'].includes(
                    data.identificationRequest.status,
                  )
                ) {
                  mutationMessage.mutate({
                    text,
                    fileId,
                    groupId: group.id,
                    studioId,
                    threadId: data?.threadId,
                    triggeredBy: userName,
                    triggeredByEmail: userEmail,
                  });
                }
              }}
              disabled={
                isLoading ||
                (data?.identificationRequest &&
                  ['resolved', 'closed'].includes(
                    data.identificationRequest.status,
                  ))
              }
            />
          </Container>
        </Overlay>,
        portalDiv,
      )
    : null;
};
